import { useState, useMemo, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
//  useUnits — a small, reusable unit-system hook
// ─────────────────────────────────────────────────────────────────────────────
//
//  Design goals
//  ------------
//  1. Reusable across programs. This file ships with the dimensions needed by
//     MnLayer (length, modulus, pressure, force). Each dimension may offer
//     several units per system; the user picks which one to display. Other
//     programs extend it by spreading UNIT_DIMENSIONS and adding their own
//     entries (see example below).
//
//  2. The stored/canonical value is ALWAYS the US-customary value. Everything
//     else (which metric system is active, and which unit within it) is only a
//     display conversion. Switching systems or units never rewrites stored
//     state, so toggling back and forth cannot accumulate rounding error:
//     going back to the original unit returns the exact original number.
//
//  3. The engine/calculations are untouched. Convert on the way in (display ->
//     US, when the user edits a field) and on the way out (US -> display, when
//     showing inputs or results).
//
//  Extending it in another program:
//
//     import { UNIT_DIMENSIONS, useUnits } from '../hooks/useUnits';
//
//     const units = useUnits({
//       dimensions: {
//         ...UNIT_DIMENSIONS,
//         temperature: {
//           base: 'degF',
//           systems: {
//             // perBase = display units per 1 base (US) unit.
//             // For an affine unit like temperature, use offset as well.
//             us:     { options: [{ symbol: 'degF', name: 'degrees Fahrenheit', perBase: 1 }] },
//             metric: { options: [{ symbol: 'degC', name: 'degrees Celsius', perBase: 5 / 9, offset: -32 * 5 / 9 }] },
//           },
//         },
//       },
//     });
//
// ─────────────────────────────────────────────────────────────────────────────

export type UnitSystem = 'us' | 'metric';

export interface UnitDef {
  /** Short label shown next to fields, e.g. "mm". Unique within a system. */
  symbol: string;
  /** Full name, e.g. "millimeters". */
  name: string;
  /**
   * Display units per 1 base (US-customary) unit.
   * display = base * perBase + offset;  base = (display - offset) / perBase.
   * For the US base unit this is 1 (with offset 0) because base === US.
   */
  perBase: number;
  /** Optional additive offset for affine scales (e.g. temperature). Default 0. */
  offset?: number;
}

export interface SystemUnits {
  /** One or more units the user may choose from in this system. */
  options: UnitDef[];
  /** Symbol of the default option. Falls back to options[0]. */
  default?: string;
}

export interface DimensionDef {
  /** The canonical stored unit (US customary). Informational only. */
  base: string;
  /** Available units per system. */
  systems: Record<UnitSystem, SystemUnits>;
}

export type UnitDimensions = Record<string, DimensionDef>;

// ── Default registry (units used by MnLayer) ────────────────────────────────
// Stored/base unit is always the US-customary one, so its perBase is 1.
export const UNIT_DIMENSIONS: UnitDimensions = {
  // Distances: thicknesses, radii, coordinates, displacements, grid ranges.
  length: {
    base: 'in.',
    systems: {
      us:     { options: [{ symbol: 'in.', name: 'inches', perBase: 1 }] },
      metric: {
        default: 'cm',
        options: [
          { symbol: 'cm', name: 'centimeters', perBase: 2.54 },
          { symbol: 'mm', name: 'millimeters', perBase: 25.4 },
          { symbol: 'm',  name: 'meters',      perBase: 0.0254 },
        ],
      },
    },
  },
  // Contact pressure, result stresses, and layer stiffness (elastic modulus) —
  // all the same physical dimension, so one selection governs them together.
  pressure: {
    base: 'psi',
    systems: {
      us:     { options: [{ symbol: 'psi', name: 'pounds per square inch', perBase: 1 }] },
      metric: {
        default: 'kg/cm²',
        options: [
          { symbol: 'kg/cm²', name: 'kilograms-force per square centimeter', perBase: 0.0703069579 },
          { symbol: 'kPa',    name: 'kilopascals',                           perBase: 6.89475729 },
          { symbol: 'MPa',    name: 'megapascals',                           perBase: 0.00689475729 },
        ],
      },
    },
  },
  // Resultant load (pressure x area). Shown in kN in metric.
  force: {
    base: 'lbf',
    systems: {
      us:     { options: [{ symbol: 'lbf', name: 'pounds-force', perBase: 1 }] },
      metric: { options: [{ symbol: 'kN',  name: 'kilonewtons',  perBase: 0.00444822162 }] },
    },
  },
};

export interface UseUnitsOptions {
  /** Override / extend the default registry. */
  dimensions?: UnitDimensions;
  /** Starting system. Defaults to 'us' (US customary). */
  initial?: UnitSystem;
}

export interface UseUnits {
  system: UnitSystem;
  setSystem: (s: UnitSystem) => void;
  toggle: () => void;
  dimensions: UnitDimensions;
  /** Stored (US) value -> current display value. */
  toDisplay: (dim: string, usValue: number) => number;
  /** Current display value -> stored (US) value. */
  fromDisplay: (dim: string, displayValue: number) => number;
  /** Short symbol for a dimension in the current system, e.g. "mm". */
  symbol: (dim: string) => string;
  /** Full name for a dimension in the current system, e.g. "millimeters". */
  name: (dim: string) => string;
  /** Units available for a dimension in a system (defaults to current). */
  options: (dim: string, system?: UnitSystem) => UnitDef[];
  /** Selected unit symbol for a dimension in a system (defaults to current). */
  selectedSymbol: (dim: string, system?: UnitSystem) => string;
  /** Choose the display unit for a dimension in a system (defaults to current). */
  setUnit: (dim: string, symbol: string, system?: UnitSystem) => void;
}

type Selection = Record<string, Record<UnitSystem, string>>;

function initialSelection(dims: UnitDimensions): Selection {
  const sel: Selection = {};
  for (const [dim, def] of Object.entries(dims)) {
    const pick = (su: SystemUnits) => su.default ?? su.options[0]?.symbol ?? '';
    sel[dim] = { us: pick(def.systems.us), metric: pick(def.systems.metric) };
  }
  return sel;
}

export function useUnits(options: UseUnitsOptions = {}): UseUnits {
  const dimensions = options.dimensions ?? UNIT_DIMENSIONS;
  const [system, setSystem] = useState<UnitSystem>(options.initial ?? 'us');
  const [selection, setSelection] = useState<Selection>(() => initialSelection(dimensions));

  const toggle = useCallback(
    () => setSystem(s => (s === 'us' ? 'metric' : 'us')),
    [],
  );

  const setUnit = useCallback(
    (dim: string, symbol: string, sys: UnitSystem = system) => {
      setSelection(prev => ({
        ...prev,
        [dim]: { ...(prev[dim] ?? { us: '', metric: '' }), [sys]: symbol },
      }));
    },
    [system],
  );

  return useMemo<UseUnits>(() => {
    const suFor = (dim: string, sys: UnitSystem): SystemUnits | null =>
      dimensions[dim]?.systems[sys] ?? null;

    const selSymbol = (dim: string, sys: UnitSystem): string => {
      const su = suFor(dim, sys);
      if (!su) return '';
      return selection[dim]?.[sys] ?? su.default ?? su.options[0]?.symbol ?? '';
    };

    // Resolve the active unit definition for a dimension in the current system.
    const defFor = (dim: string): UnitDef | null => {
      const su = suFor(dim, system);
      if (!su) return null; // unknown / dimensionless -> no conversion
      const sym = selSymbol(dim, system);
      return su.options.find(o => o.symbol === sym) ?? su.options[0] ?? null;
    };

    return {
      system,
      setSystem,
      toggle,
      dimensions,
      toDisplay: (dim, us) => {
        const d = defFor(dim);
        if (!d) return us;
        return us * d.perBase + (d.offset ?? 0);
      },
      fromDisplay: (dim, disp) => {
        const d = defFor(dim);
        if (!d) return disp;
        return (disp - (d.offset ?? 0)) / d.perBase;
      },
      symbol: (dim) => defFor(dim)?.symbol ?? '',
      name:   (dim) => defFor(dim)?.name ?? '',
      options: (dim, sys = system) => suFor(dim, sys)?.options ?? [],
      selectedSymbol: (dim, sys = system) => selSymbol(dim, sys),
      setUnit,
    };
  }, [system, dimensions, selection, toggle, setUnit]);
}